import type {
  ColorFieldDefinition,
  LandingFieldDefinition,
  ThemeLanding,
} from "containers/theme-setter/types";
import { getLandingFieldPreview } from "containers/theme-setter/field-previews";

export const fontFieldDefinitions = [
  { label: "Body font", key: "body" as const },
  { label: "Heading font", key: "heading" as const },
];

const CAUTION_COLOR_TOKENS: readonly string[] = [
  "yellow",
  "lightGreen",
  "green",
  "red",
  "blue",
  "darkestBlue",
  "darkBlue",
  "darkGray",
  "lightGray",
  "lighterGray",
] as const;

const COLOR_TOKEN_ORDER: readonly string[] = [
  "primary",
  "primaryHover",
  "border",
  "statisticTitle",
  "simulatorBg",
  "away",
  "home",
  ...CAUTION_COLOR_TOKENS,
] as const;

const CAUTION_COLOR_SET = new Set<string>(CAUTION_COLOR_TOKENS);
const GENERAL_COLOR_ORDER = COLOR_TOKEN_ORDER.filter(
  (token) => !CAUTION_COLOR_SET.has(token)
);
const CAUTION_COLOR_ORDER = COLOR_TOKEN_ORDER.filter((token) =>
  CAUTION_COLOR_SET.has(token)
);

const buildColorDefinition = (
  token: string,
  group: ColorFieldDefinition["group"],
  colors: Record<string, string>
): ColorFieldDefinition => ({
  token,
  value: colors[token] ?? "",
  group,
});

export const buildColorFieldDefinitions = (
  colors: Record<string, string>
): ColorFieldDefinition[] => {
  const tokens = Object.keys(colors);
  const generalExtras = tokens
    .filter(
      (token) =>
        !GENERAL_COLOR_ORDER.includes(token) && !CAUTION_COLOR_SET.has(token)
    )
    .sort((a, b) => a.localeCompare(b));
  const cautionExtras = tokens.filter(
    (token) =>
      !CAUTION_COLOR_ORDER.includes(token) && CAUTION_COLOR_SET.has(token)
  );

  const generalTokens = [
    ...GENERAL_COLOR_ORDER.filter((token) => token in colors),
    ...generalExtras,
  ];
  const cautionTokens = [
    ...CAUTION_COLOR_ORDER.filter((token) => token in colors),
    ...cautionExtras,
  ];

  return [
    ...generalTokens.map((token) =>
      buildColorDefinition(token, "standard", colors)
    ),
    ...cautionTokens.map((token) =>
      buildColorDefinition(token, "caution", colors)
    ),
  ];
};

type LandingFieldDefinitionConfig = Omit<LandingFieldDefinition, "preview"> & {
  disablePreview?: boolean;
};

export const buildLandingFieldDefinitions = (
  landing: ThemeLanding
): LandingFieldDefinition[] => {
  const landingLogos = {
    main: landing.logos?.main ?? {},
    simple: landing.logos?.simple ?? {},
  };
  const landingMetadata = landing.metadata ?? {};

  const definitions: LandingFieldDefinitionConfig[] = [
    {
      label: "Hero background",
      path: "landing.heroBackground",
      value: landing.heroBackground ?? "",
      supportsUpload: true,
    },
    {
      label: "Hero right image",
      path: "landing.heroRightImage",
      value: landing.heroRightImage ?? "",
      supportsUpload: true,
    },
    {
      label: "Main logo src",
      path: "landing.logos.main.src",
      value: landingLogos.main?.src ?? "",
      supportsUpload: true,
    },
    {
      label: "Main logo alt",
      path: "landing.logos.main.alt",
      value: landingLogos.main?.alt ?? "",
      disablePreview: true,
    },
    {
      label: "Simple logo src",
      path: "landing.logos.simple.src",
      value: landingLogos.simple?.src ?? "",
      supportsUpload: true,
    },
    {
      label: "Simple logo alt",
      path: "landing.logos.simple.alt",
      value: landingLogos.simple?.alt ?? "",
      disablePreview: true,
    },
    {
      label: "Page title",
      path: "landing.metadata.pageTitle",
      value: landingMetadata.pageTitle ?? "",
    },
    {
      label: "Favicon URL",
      path: "landing.metadata.faviconHref",
      value: landingMetadata.faviconHref ?? "",
      supportsUpload: true,
    },
    {
      label: "Hero primary text",
      path: "landing.metadata.heroPrimaryShapeText",
      value: landingMetadata.heroPrimaryShapeText ?? "",
      colSpan: 2,
    },
  ];

  return definitions.map(({ disablePreview, ...definition }) => ({
    ...definition,
    preview: disablePreview
      ? undefined
      : getLandingFieldPreview(definition.path, definition.label),
  }));
};
